import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompetition, AdminAccessGate } from '../context/CompetitionContext';
import { Discipline } from '../firebase/competitionService';

const DISCIPLINE_OPTIONS: {
  key: Discipline;
  label: string;
  description: string;
  adminPath: string;
  color: string;
  icon: string;
}[] = [
  {
    key:         'boulder',
    label:       'Boulder',
    description: 'Enter zone and top attempts per problem',
    adminPath:   'boulder-admin',
    color:       'from-green-500 to-teal-600',
    icon:        '🪨',
  },
  {
    key:         'lead',
    label:       'Lead / Top-rope',
    description: 'Record holds, plus, TOP and DNF per route',
    adminPath:   'lead-admin',
    color:       'from-blue-500 to-indigo-600',
    icon:        '🧗',
  },
  {
    key:         'speed',
    label:       'Speed',
    description: 'Manage heats, lanes, and times',
    adminPath:   'speed-admin',
    color:       'from-yellow-500 to-orange-500',
    icon:        '⚡',
  },
];

const JuryHubInner: React.FC = () => {
  const navigate = useNavigate();
  const { competitionId, metadata, adminSecret } = useCompetition();

  const secretSuffix = adminSecret ? `?secret=${adminSecret}` : '';
  const enabledDisciplines = metadata?.disciplines ?? (['boulder', 'lead', 'speed'] as Discipline[]);
  const visibleOptions = DISCIPLINE_OPTIONS.filter(d => enabledDisciplines.includes(d.key));
  const hasLead = enabledDisciplines.includes('lead');

  const goTo = (path: string) =>
    navigate(`/competition/${competitionId}/${path}${secretSuffix}`);

  return (
    <div className="min-h-screen bg-dark text-white">
      <div className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-4xl font-bold text-center mb-2 tracking-tight">
          {metadata?.name ?? competitionId} — Jury Panel
        </h1>
        <p className="text-center text-gray-400 mb-12 text-lg">
          Select a discipline to open the admin panel
        </p>

        <div className="grid gap-6 sm:grid-cols-3">
          {visibleOptions.map(d => (
            <button
              key={d.key}
              onClick={() => goTo(d.adminPath)}
              className={`bg-gradient-to-br ${d.color} rounded-2xl p-6 text-left shadow-lg hover:scale-105 active:scale-95 transition-transform duration-150 focus:outline-none focus:ring-2 focus:ring-white`}
            >
              <div className="text-4xl mb-3">{d.icon}</div>
              <h2 className="text-xl font-bold mb-1">{d.label}</h2>
              <p className="text-sm text-white/80 leading-snug">{d.description}</p>
            </button>
          ))}
          {hasLead && (
            <button
              onClick={() => goTo('lead-jury-print')}
              className="bg-gradient-to-br from-slate-500 to-slate-700 rounded-2xl p-6 text-left shadow-lg hover:scale-105 active:scale-95 transition-transform duration-150 focus:outline-none focus:ring-2 focus:ring-white"
            >
              <div className="text-4xl mb-3">🖨</div>
              <h2 className="text-xl font-bold mb-1">Lead Print Sheet</h2>
              <p className="text-sm text-white/80 leading-snug">Printable backup score sheet for all lead groups</p>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const JuryHubPage: React.FC = () => (
  <AdminAccessGate>
    <JuryHubInner />
  </AdminAccessGate>
);

export default JuryHubPage;
